import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import {
  DEFAULT_LOCALE,
  LANGUAGE_STORAGE_KEY,
  SUPPORTED_LOCALES,
  type AppLocale,
  normalizeLocale,
  resolvePreferredLocale,
} from './localeTypes';
import commonEn from './locales/en/common.json';
import authEn from './locales/en/auth.json';
import dashboardEn from './locales/en/dashboard.json';
import workspaceEn from './locales/en/workspace.json';
import platformAdminEn from './locales/en/platformAdmin.json';
import billingEn from './locales/en/billing.json';
import alertsEn from './locales/en/alerts.json';
import sitesEn from './locales/en/sites.json';
import siteEn from './locales/en/site.json';
import scanDetailEn from './locales/en/scanDetail.json';
import reportsEn from './locales/en/reports.json';
import marketingEn from './locales/en/marketing.json';
import helpEn from './locales/en/help.json';
import partnerPortalEn from './locales/en/partnerPortal.json';
import broadcastsEn from './locales/en/broadcasts.json';
import commonDe from './locales/de/common.json';
import authDe from './locales/de/auth.json';
import dashboardDe from './locales/de/dashboard.json';
import workspaceDe from './locales/de/workspace.json';
import platformAdminDe from './locales/de/platformAdmin.json';
import billingDe from './locales/de/billing.json';
import alertsDe from './locales/de/alerts.json';
import sitesDe from './locales/de/sites.json';
import siteDe from './locales/de/site.json';
import scanDetailDe from './locales/de/scanDetail.json';
import reportsDe from './locales/de/reports.json';
import marketingDe from './locales/de/marketing.json';
import helpDe from './locales/de/help.json';
import broadcastsDe from './locales/de/broadcasts.json';
import commonFr from './locales/fr/common.json';
import authFr from './locales/fr/auth.json';
import dashboardFr from './locales/fr/dashboard.json';
import workspaceFr from './locales/fr/workspace.json';
import platformAdminFr from './locales/fr/platformAdmin.json';
import billingFr from './locales/fr/billing.json';
import alertsFr from './locales/fr/alerts.json';
import sitesFr from './locales/fr/sites.json';
import siteFr from './locales/fr/site.json';
import scanDetailFr from './locales/fr/scanDetail.json';
import reportsFr from './locales/fr/reports.json';
import marketingFr from './locales/fr/marketing.json';
import helpFr from './locales/fr/help.json';
import broadcastsFr from './locales/fr/broadcasts.json';
import commonEs from './locales/es/common.json';
import authEs from './locales/es/auth.json';
import dashboardEs from './locales/es/dashboard.json';
import workspaceEs from './locales/es/workspace.json';
import platformAdminEs from './locales/es/platformAdmin.json';
import billingEs from './locales/es/billing.json';
import alertsEs from './locales/es/alerts.json';
import sitesEs from './locales/es/sites.json';
import siteEs from './locales/es/site.json';
import scanDetailEs from './locales/es/scanDetail.json';
import reportsEs from './locales/es/reports.json';
import marketingEs from './locales/es/marketing.json';
import helpEs from './locales/es/help.json';
import broadcastsEs from './locales/es/broadcasts.json';
import commonIt from './locales/it/common.json';
import authIt from './locales/it/auth.json';
import dashboardIt from './locales/it/dashboard.json';
import workspaceIt from './locales/it/workspace.json';
import platformAdminIt from './locales/it/platformAdmin.json';
import billingIt from './locales/it/billing.json';
import alertsIt from './locales/it/alerts.json';
import sitesIt from './locales/it/sites.json';
import siteIt from './locales/it/site.json';
import scanDetailIt from './locales/it/scanDetail.json';
import reportsIt from './locales/it/reports.json';
import marketingIt from './locales/it/marketing.json';
import helpIt from './locales/it/help.json';
import broadcastsIt from './locales/it/broadcasts.json';

const baseResources = {
  en: {
    common: commonEn,
    auth: authEn,
    dashboard: dashboardEn,
    workspace: workspaceEn,
    platformAdmin: platformAdminEn,
    billing: billingEn,
    alerts: alertsEn,
    sites: sitesEn,
    site: siteEn,
    scanDetail: scanDetailEn,
    reports: reportsEn,
    marketing: marketingEn,
    help: helpEn,
    partnerPortal: partnerPortalEn,
    broadcasts: broadcastsEn,
  },
  de: {
    common: commonDe,
    auth: authDe,
    dashboard: dashboardDe,
    workspace: workspaceDe,
    platformAdmin: platformAdminDe,
    billing: billingDe,
    alerts: alertsDe,
    sites: sitesDe,
    site: siteDe,
    scanDetail: scanDetailDe,
    reports: reportsDe,
    marketing: marketingDe,
    help: helpDe,
    partnerPortal: partnerPortalEn,
    broadcasts: broadcastsDe,
  },
  fr: {
    common: commonFr,
    auth: authFr,
    dashboard: dashboardFr,
    workspace: workspaceFr,
    platformAdmin: platformAdminFr,
    billing: billingFr,
    alerts: alertsFr,
    sites: sitesFr,
    site: siteFr,
    scanDetail: scanDetailFr,
    reports: reportsFr,
    marketing: marketingFr,
    help: helpFr,
    partnerPortal: partnerPortalEn,
    broadcasts: broadcastsFr,
  },
  es: {
    common: commonEs,
    auth: authEs,
    dashboard: dashboardEs,
    workspace: workspaceEs,
    platformAdmin: platformAdminEs,
    billing: billingEs,
    alerts: alertsEs,
    sites: sitesEs,
    site: siteEs,
    scanDetail: scanDetailEs,
    reports: reportsEs,
    marketing: marketingEs,
    help: helpEs,
    partnerPortal: partnerPortalEn,
    broadcasts: broadcastsEs,
  },
  it: {
    common: commonIt,
    auth: authIt,
    dashboard: dashboardIt,
    workspace: workspaceIt,
    platformAdmin: platformAdminIt,
    billing: billingIt,
    alerts: alertsIt,
    sites: sitesIt,
    site: siteIt,
    scanDetail: scanDetailIt,
    reports: reportsIt,
    marketing: marketingIt,
    help: helpIt,
    partnerPortal: partnerPortalEn,
    broadcasts: broadcastsIt,
  },
} as const;

type LocaleResourceBundle = (typeof baseResources)[keyof typeof baseResources];

function buildNavigationNamespace(bundle: LocaleResourceBundle) {
  return {
    ...bundle.common.navigation,
    shell: bundle.common.shell,
    groups: bundle.common.sidebar.groups,
    items: bundle.common.sidebar.items,
    brandAriaLabel: bundle.common.sidebar.brandAriaLabel,
    brandMeta: bundle.common.sidebar.brandMeta,
    planBadge: bundle.common.sidebar.planBadge,
    alertsUpgrade: bundle.common.sidebar.alertsUpgrade,
  };
}

function buildDialogsNamespace(bundle: LocaleResourceBundle) {
  return {
    actions: bundle.common.actions,
    featureLock: bundle.common.featureLockModal,
    addSite: bundle.sites.modal,
    admin: bundle.platformAdmin.modals,
  };
}

function buildValidationNamespace(bundle: LocaleResourceBundle) {
  return {
    errors: bundle.common.errors,
    login: bundle.auth.login.errors,
    register: bundle.auth.register.errors,
    acceptInvite: bundle.auth.acceptInvite.errors,
  };
}

function buildNoticesNamespace(bundle: LocaleResourceBundle) {
  return {
    billing: bundle.billing.feedback,
    schedule: bundle.site.schedule.feedback,
    alertPreferences: bundle.site.alertPreferences.feedback,
    authProfiles: bundle.site.authProfile.feedback,
    onboarding: {
      complete: bundle.dashboard.onboarding.feedbackComplete,
      reviewed: bundle.dashboard.onboarding.feedbackReviewed,
      finished: bundle.dashboard.onboarding.feedbackFinished,
      failed: bundle.dashboard.onboarding.feedbackFailed,
    },
    workspace: {
      profile: {
        saved: bundle.workspace.profile.saved,
        saveFailed: bundle.workspace.profile.saveFailed,
      },
      invites: {
        created: bundle.workspace.invites.created,
        createFailed: bundle.workspace.invites.createFailed,
        copied: bundle.workspace.invites.copied,
        copyFallback: bundle.workspace.invites.copyFallback,
        copyFailed: bundle.workspace.invites.copyFailed,
        revoked: bundle.workspace.invites.revoked,
        revokeFailed: bundle.workspace.invites.revokeFailed,
      },
      preferences: {
        saved: bundle.workspace.preferences.saved,
        saveFailed: bundle.workspace.preferences.saveFailed,
      },
      members: {
        roleUpdated: bundle.workspace.members.roleUpdated,
        deactivated: bundle.workspace.members.deactivated,
        reactivated: bundle.workspace.members.reactivated,
        roleFailed: bundle.workspace.members.roleFailed,
        statusFailed: bundle.workspace.members.statusFailed,
      },
    },
  };
}

function buildQueueNamespace(bundle: LocaleResourceBundle) {
  return {
    title: bundle.sites.views.queue.label,
    button: bundle.sites.views.queue.button,
    description: bundle.sites.views.queue.description,
    sectionTitle: bundle.site.scanHistory.sectionTitle,
    sectionSubtitle: bundle.site.scanHistory.sectionSubtitle,
    loading: bundle.site.scanHistory.loading,
    emptyTitle: bundle.site.scanHistory.emptyTitle,
    emptyDescription: bundle.site.scanHistory.emptyDescription,
    runFirstScan: bundle.site.scanHistory.runFirstScan,
    statuses: bundle.site.scanStatuses,
  };
}

function buildIssuesNamespace(bundle: LocaleResourceBundle) {
  return {
    title: bundle.sites.views.issues.label,
    button: bundle.sites.views.issues.button,
    description: bundle.sites.views.issues.description,
    section: bundle.site.issues,
    workflowStates: bundle.dashboard.workflowStates,
  };
}

function buildEvidenceNamespace(bundle: LocaleResourceBundle) {
  return {
    title: bundle.sites.views.evidence.label,
    button: bundle.sites.views.evidence.button,
    description: bundle.sites.views.evidence.description,
    section: bundle.site.evidence,
  };
}

function buildAuthProfilesNamespace(bundle: LocaleResourceBundle) {
  return {
    title: bundle.sites.views.auth.label,
    button: bundle.sites.views.auth.button,
    description: bundle.sites.views.auth.description,
    session: bundle.site.authSession,
    profile: bundle.site.authProfile,
    modes: bundle.site.auth.modes,
    failures: bundle.site.auth.failures,
  };
}

function buildSchedulesNamespace(bundle: LocaleResourceBundle) {
  return {
    title: bundle.sites.views.schedules.label,
    button: bundle.sites.views.schedules.button,
    description: bundle.sites.views.schedules.description,
    schedule: bundle.site.schedule,
    jobs: bundle.site.scheduledJobs,
    alertPreferences: bundle.site.alertPreferences,
  };
}

function withBundleBAliases(bundle: LocaleResourceBundle) {
  return {
    ...bundle,
    navigation: buildNavigationNamespace(bundle),
    dialogs: buildDialogsNamespace(bundle),
    validation: buildValidationNamespace(bundle),
    onboarding: bundle.dashboard.onboarding,
    notices: buildNoticesNamespace(bundle),
    queue: buildQueueNamespace(bundle),
    issues: buildIssuesNamespace(bundle),
    evidence: buildEvidenceNamespace(bundle),
    authProfiles: buildAuthProfilesNamespace(bundle),
    schedules: buildSchedulesNamespace(bundle),
    admin: bundle.platformAdmin,
  };
}

const resources = {
  en: withBundleBAliases(baseResources.en),
  de: withBundleBAliases(baseResources.de),
  fr: withBundleBAliases(baseResources.fr),
  es: withBundleBAliases(baseResources.es),
  it: withBundleBAliases(baseResources.it),
} as const;

function getSavedLocalePreference(): AppLocale | null {
  if (typeof window === 'undefined') return null;

  try {
    const stored = window.localStorage.getItem(LANGUAGE_STORAGE_KEY);
    return stored ? normalizeLocale(stored) : null;
  } catch {
    return null;
  }
}

function getBrowserLocale(): AppLocale {
  if (typeof navigator === 'undefined') return DEFAULT_LOCALE;

  return resolvePreferredLocale({
    browserLanguages: [navigator.language, ...(Array.isArray(navigator.languages) ? navigator.languages : [])],
    fallbackLocale: DEFAULT_LOCALE,
  });
}

export function resolveInitialLocale(): AppLocale {
  return getSavedLocalePreference() ?? getBrowserLocale();
}

export async function changeAppLanguage(locale: string): Promise<void> {
  const nextLocale = normalizeLocale(locale);
  await i18n.changeLanguage(nextLocale);
}

const initialLocale = resolveInitialLocale();

if (typeof document !== 'undefined') {
  document.documentElement.lang = initialLocale;
}

void i18n.use(initReactI18next).init({
  resources,
  lng: initialLocale,
  fallbackLng: DEFAULT_LOCALE,
  supportedLngs: [...SUPPORTED_LOCALES],
  defaultNS: 'common',
  ns: [
    'common',
    'auth',
    'dashboard',
    'workspace',
    'platformAdmin',
    'billing',
    'alerts',
    'sites',
    'site',
    'scanDetail',
    'reports',
    'marketing',
    'help',
    'partnerPortal',
    'broadcasts',
    'navigation',
    'dialogs',
    'validation',
    'onboarding',
    'notices',
    'queue',
    'issues',
    'evidence',
    'authProfiles',
    'schedules',
    'admin',
  ],
  interpolation: {
    escapeValue: false,
  },
  react: {
    useSuspense: false,
  },
  returnEmptyString: false,
  parseMissingKeyHandler: (key) => key,
});

i18n.on('languageChanged', (language) => {
  const normalized = normalizeLocale(language);

  if (typeof document !== 'undefined') {
    document.documentElement.lang = normalized;
  }

  if (typeof window !== 'undefined') {
    try {
      window.localStorage.setItem(LANGUAGE_STORAGE_KEY, normalized);
    } catch {
      // Ignore storage failures in restricted environments.
    }
  }
});

export default i18n;
